import { ApolloError } from "@apollo/client";
import { Flex, Icon } from "@chakra-ui/react";
import React from "react";
import { HiOutlineSparkles } from "react-icons/hi2";

import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { useSendGAEvent } from "../../../utils/googleAnalytics";
import {
  AiInterviewCallFragment,
  CallNoteFragment,
  TranscriptSegmentFragment,
} from "../../graphql";
import useFeatureFlag from "../../graphql/hooks/useFeatureFlag";
import { ClipRange } from "../../types";
import MainTabs, { MainTab } from "../AiRecording/MainTabs";
import EvaluationTab from "../AiRecording/Tabs/EvaluationTab";
import NotesTab from "../AiRecording/Tabs/NotesTab";
import SignalsTab from "../AiRecording/Tabs/SignalsTab";
import TranscriptTab from "../AiRecording/Tabs/TranscriptTab";
import {
  AskPanel,
  introMessageForInterviews,
  suggestedInterviewMessages,
} from "../Candidate/Ask/AskPanel";
import { MediaPlayerInterface } from "../MediaPlayer/useMediaPlayer";
import { useAsk2Enabled } from "../Recording/SidebarTabs/Ai/Ask/useAsk2Enabled";
import { InterviewTab } from "./utils";

type AiInterviewMainProps = {
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "duration"
    | "canEdit"
    | "canShare"
    | "canShareExternal"
    | "clipsCount"
    | "position"
    | "speakers"
    | "transcriptLanguageCode"
    | "isProcessing"
    | "startTime"
    | "endTime"
    | "status"
    | "streamableAudio"
    | "aiInterview"
  >;
  notes: CallNoteFragment[];
  notesLoading: boolean;
  notesError?: ApolloError;
  transcript: TranscriptSegmentFragment[];
  transcriptLoading: boolean;
  transcriptError?: ApolloError;
  player: MediaPlayerInterface;
  onSeek(t: number): void;
  onCreateClipClick?(clipRange: ClipRange): void;
  currentTab?: InterviewTab;
  onTabChange?(t: InterviewTab): void;
  autoScrollEnabled: boolean;
  setAutoScrollEnabled(enabled: boolean): void;
  notesForCurrentUserOnly: boolean;
};

const AiInterviewMain: React.FC<AiInterviewMainProps> = ({
  call,
  notes,
  notesLoading,
  notesError,
  transcript,
  transcriptLoading,
  transcriptError,
  player,
  onSeek,
  onCreateClipClick,
  currentTab,
  onTabChange,
  autoScrollEnabled,
  setAutoScrollEnabled,
  notesForCurrentUserOnly,
}) => {
  const isSmallScreen = useIsSmallScreen();
  const ask2Enabled = useAsk2Enabled();
  const signalsEnabled = useFeatureFlag(
    "ai_interviewer:interview_integrity:ui"
  );
  const sendGAEvent = useSendGAEvent();

  return (
    <MainTabs
      id="call-main"
      currentTab={currentTab}
      onTabChange={(newTab) => {
        if (newTab !== currentTab) {
          sendGAEvent("main_tab_change", "call_review", newTab);
        }
        onTabChange?.(newTab);
      }}
    >
      <MainTab
        id={InterviewTab.EVALUATION}
        padBottom
        displayName={
          <Flex alignItems="center" textColor="purple.600">
            <Icon
              as={HiOutlineSparkles}
              color="purple.600"
              h="16px"
              mr="3px"
              w="16px"
            />
            {isSmallScreen && ask2Enabled ? "Eval." : "Evaluation"}
          </Flex>
        }
      >
        <EvaluationTab call={call} onPlayMedia={player.play} />
      </MainTab>

      {isSmallScreen && (
        <MainTab
          id={InterviewTab.NOTES}
          displayName={ask2Enabled ? "U. notes" : "User Notes"}
          padBottom
        >
          <NotesTab
            call={call}
            notes={notes}
            loading={notesLoading}
            error={notesError}
            onClickTimestamp={onSeek}
            playerTime={player.time}
            notesForCurrentUserOnly={notesForCurrentUserOnly}
            overflow="hidden"
          />
        </MainTab>
      )}

      <MainTab id={InterviewTab.TRANSCRIPT} displayName="Transcript">
        <TranscriptTab
          call={call}
          notes={notes}
          transcript={transcript}
          loading={transcriptLoading}
          error={transcriptError}
          autoScrollEnabled={autoScrollEnabled}
          setAutoScrollEnabled={setAutoScrollEnabled}
          player={player}
          onCreateClipClick={onCreateClipClick}
          speakerOptions={call.speakers}
        />
      </MainTab>

      {signalsEnabled && (
        <MainTab id={InterviewTab.SIGNALS} displayName="Signals" padBottom>
          <SignalsTab
            integritySignals={call.aiInterview?.integritySignals ?? []}
            interviewStartTime={call.startTime}
            interviewDuration={call.duration}
            onSeek={onSeek}
          />
        </MainTab>
      )}

      {isSmallScreen && ask2Enabled && (
        <MainTab
          id={InterviewTab.ASSIST}
          displayName={
            <Flex alignItems="center" textColor="purple.600">
              <Icon
                as={HiOutlineSparkles}
                color="purple.600"
                h="16px"
                mr="3px"
                w="16px"
              />
              Assist.
            </Flex>
          }
        >
          <AskPanel
            requestPath={`/interview/${call.id}/ask2`}
            onClickTimestamp={onSeek}
            onClose={() => {
              // No-op for inline mode
            }}
            hidden={false}
            inline
            suggestedMessages={suggestedInterviewMessages}
            introMessage={introMessageForInterviews}
            eventCategory="call_review"
          />
        </MainTab>
      )}
    </MainTabs>
  );
};

export default AiInterviewMain;
